import { X } from "lucide-react";

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export const OrderDetailModal = ({ order, onClose, onUpdateStatus, onDelete }) => {
  if (!order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-detail-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between bg-gray-50 rounded-t-2xl">
          <h2 className="font-serif text-2xl">Order Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-sm text-gray-500 mb-1">Customer</p>
            <p className="font-medium text-lg">{order.customer_name}</p>
            <p className="text-gray-600">{order.email}</p>
            <p className="text-gray-600">{order.phone}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Event Date</p>
              <p className="font-medium">{order.event_date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Event Type</p>
              <p className="font-medium capitalize">{order.event_type}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Event Address</p>
            <p className="font-medium">{order.event_address}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Items</p>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              {order.items.map((item) => (
                <div key={item.product_name} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.product_name}</span>
                  <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-red-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>${order.subtotal.toFixed(2)}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-${order.discount.toFixed(2)}</span>
              </div>
            )}
            {(order.tax || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Tax (6%)</span>
                <span>${order.tax.toFixed(2)}</span>
              </div>
            )}
            {(order.service_fee || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Service Fee (4%)</span>
                <span>${order.service_fee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-red-200">
              <span>Total</span>
              <span className="text-celebration-red">${order.total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 pt-1">+ Delivery fee varies by location, added to final invoice</p>
          </div>
          {order.promo_code && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Promo Code</p>
              <p className="font-medium">{order.promo_code}</p>
            </div>
          )}
          {order.special_requests && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Special Requests</p>
              <p className="text-gray-700">{order.special_requests}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-1">Status</p>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100'}`}>
              {order.status}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Order Date</p>
            <p className="text-sm">{formatDate(order.created_at)}</p>
          </div>
          <div className="flex gap-2 pt-2">
            {order.status === 'pending' && (
              <button
                onClick={() => { onUpdateStatus(order.id, 'confirmed'); onClose(); }}
                className="flex-1 btn-primary"
                data-testid="confirm-order-btn"
              >
                Confirm Order
              </button>
            )}
            {order.status === 'confirmed' && (
              <button
                onClick={() => { onUpdateStatus(order.id, 'completed'); onClose(); }}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-full font-bold hover:bg-green-700 transition-colors"
                data-testid="complete-order-btn"
              >
                Mark Completed
              </button>
            )}
            <button
              onClick={() => onDelete(order.id)}
              className="px-6 py-3 border border-red-300 text-red-500 rounded-full font-medium hover:bg-red-50 transition-colors"
              data-testid="delete-order-modal-btn"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
